import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarSubsidiariesApi } from "../../http/carSubsidiaries/api";
import { CarSubsidiaryNoCarDepency } from "../../http/carSubsidiaries/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric, isWithinLast48h } from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import { getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import NewItemIcon from "../ui/NewItemIcon";
import Table from "../ui/Table";
import CarSubsidiaryHistoryDialog from "./CarSubsidiaryHistoryDialog";
import CreateCarSubsidiaryButton from "./CreateCarSubsidiaryButton";
import DeleteCarSubsidiaryDialog from "./DeleteCarSubsidiaryDialog";
import EditCarSubsidiaryDialog from "./EditCarSubsidiaryDialog";

const CAR_SUBSIDIARY_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "startDate",
  "endDate",
  "subsidiary.id",
  "subsidiary.name",
  "versionNumber",
] as const;
type CarSubsidiaryField = (typeof CAR_SUBSIDIARY_FIELDS)[number];
type CarSubsidiarySearch = NestedPick<CarSubsidiaryNoCarDepency, CarSubsidiaryField>;

interface CarSubsidiariesDialogProps {
  carId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const CarSubsidiariesDialog: FC<CarSubsidiariesDialogProps> = ({ carId, onClose, onUpdate }) => {
  const i18n = useI18n();
  const carSubsidiariesApi = useCarSubsidiariesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "cs" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const [itemToEdit, setItemToEdit] = useState<CarSubsidiarySearch>();
  const [itemToDelete, setItemToDelete] = useState<CarSubsidiarySearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<CarSubsidiarySearch>();
  const [showNewCarSubsidiary, setShowNewCarSubsidiary] = useState<boolean>();

  const query: PaginationQueryWithFields<CarSubsidiarySearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_SUBSIDIARY_FIELDS,
  };

  const result = carSubsidiariesApi.useCarSubsidiaries(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
  }, [result.error]);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.carSubsidiaries.plural}
        preTitleButton={<Button buttonProps={{ onClick: () => setShowNewCarSubsidiary(true) }} glyph={SvgAdd} />}
      >
        <Table<CarSubsidiarySearch>
          filters={filters}
          head={
            <>
              <Table.Th
                title={i18n.t.subsidiaries.singular}
                fieldName="carSubsidiary.subsidiary"
                order={orderOptions}
              />
              <Table.Th title={i18n.t.common.startDate} fieldName="carSubsidiary.startDate" order={orderOptions} />
              <Table.Th title={i18n.t.common.endDate} fieldName="carSubsidiary.endDate" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.subsidiary.name}
              </td>
              <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
              <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
            </>
          )}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getHistoryAction(i18n, () => setItemToShowVersions(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToDelete && (
        <DeleteCarSubsidiaryDialog
          carSubsidiaryId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={() => setItemToDelete(undefined)}
          onUpdate={onUpdate}
        />
      )}

      {!!itemToEdit && (
        <EditCarSubsidiaryDialog
          carSubsidiaryId={itemToEdit.id}
          onClose={() => setItemToEdit(undefined)}
          onUpdate={onUpdate}
        />
      )}

      {!!itemToShowVersions && (
        <CarSubsidiaryHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!showNewCarSubsidiary && (
        <CreateCarSubsidiaryButton carId={carId} onClose={() => setShowNewCarSubsidiary(false)} onUpdate={onUpdate} />
      )}
    </>
  );
};

export default CarSubsidiariesDialog;
